import {FormControl} from "@luciad/ria-sample-common/components/FormControl";
import {useMapContext} from "@luciad/ria-sample-common/hooks/useMapContext";
import {useToastContext} from "@luciad/ria-sample-common/hooks/useToast";
import {WFSCapabilities} from "@luciad/ria/model/capabilities/WFSCapabilities";
import {WFSCapabilitiesFeatureType} from "@luciad/ria/model/capabilities/WFSCapabilitiesFeatureType";
import React, {ChangeEvent, useRef, useState} from "react";
import {addLayer} from "../DataFormatsUtil";
import {useWizard} from "../hooks/useWizard";
import {WFSDataLoader} from "../loaders/WFSDataLoader";
import {ConnectToURLPage} from "./ConnectToURLPage";
import {WizardPreviousNextButtons} from "./WizardPreviousNextButtons";

interface Props {
  onPrevious: () => void;
  onReset: () => void;
}

type WFSWizardPageKey = "enterUrl" | "loading" | "selectFeatureTypes";

export const ConnectToWFSWizard = ({onPrevious, onReset}: Props) => {
  const {currentPageKey, previousPageKey, changePage, resetWizard} = useWizard<WFSWizardPageKey>("enterUrl", onReset);
  const [capabilities, setCapabilities] = useState<WFSCapabilities | null>(null);
  const urlRef = useRef<string>("");

  const {map} = useMapContext();
  const {showErrorToast} = useToastContext();

  const handleURLSubmit = (url: string) => {
    urlRef.current = url;
    changePage("loading");
    WFSCapabilities.fromURL(url).then(capabilities => {
      setCapabilities(capabilities);
      changePage("selectFeatureTypes");
    }).catch(error => {
      changePage("enterUrl");
      showErrorToast("Could not connect with the WFS server", error);
    });
  }

  const handleWFSFeatureTypeSelect = (wfsFeatureType: WFSCapabilitiesFeatureType) => {
    if (capabilities) {
      try {
        const {name, title} = wfsFeatureType;
        const layer = WFSDataLoader.createLayer(urlRef.current, name, title);
        addLayer(map, layer).catch(error => {
          showErrorToast("Could not add WFS layer", error);
        });
      } catch (error) {
        showErrorToast("Could not create WFS layer", error);
      } finally {
        resetWizard();
      }
    }
  }

  switch (currentPageKey) {
  case "enterUrl": {
    return <ConnectToURLPage defaultValue="https://sampleservices.luciad.com/wfs"
                             onSubmit={handleURLSubmit}
                             onPrevious={onPrevious}
    />;
  }
  case "loading": {
    return <h1>Loading WFS capabilities...</h1>;
  }
  case "selectFeatureTypes": {
    return <SelectWFSFeatureType capabilities={capabilities!}
                                 onSelect={handleWFSFeatureTypeSelect}
                                 onPrevious={() => changePage(previousPageKey)}/>
  }
  }
}

interface SelectWFSFeatureTypeProps {
  capabilities: WFSCapabilities;
  onSelect: (featureType: WFSCapabilitiesFeatureType) => void;
  onPrevious: () => void;
}

const SelectWFSFeatureType = ({capabilities, onSelect, onPrevious}: SelectWFSFeatureTypeProps) => {
  const featureTypes = capabilities.featureTypes;
  const [selectedFeatureType, setSelectedFeatureType] = useState<WFSCapabilitiesFeatureType>(featureTypes[0]);

  const handleSelectionChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    const selectedFeatureTypeName = ev.target.value;
    const selectedFeatureType = featureTypes.find(f => f.name === selectedFeatureTypeName);
    if (selectedFeatureType) {
      setSelectedFeatureType(selectedFeatureType);
    }
  }

  const handleAdd = () => {
    onSelect(selectedFeatureType);
  }

  return (
      <div>
        <div>
          <FormControl>
            <label htmlFor="featureType">
              Feature type
            </label>
            <select name="featureType" value={selectedFeatureType.name} onChange={handleSelectionChange}>
              {featureTypes.map(featureType => {
                return <option key={featureType.name} value={featureType.name}>{featureType.title}</option>;
              })}
            </select>
          </FormControl>
        </div>
        <WizardPreviousNextButtons onPrevious={onPrevious} onNext={handleAdd} nextLabel="Add"/>
      </div>
  )

}